import { z } from 'zod'

import { cap } from '../../../../State'
import { translations } from '../../../../translations'

const language = cap.language.state || 'IT'
const t = translations[language]

const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/

const parseDate = (str) => {
  if (!str || !dateRegex.test(str)) return null
  const [d, m, y] = str.split('/')
  const date = new Date(Number(y), Number(m) - 1, Number(d))
  return Number.isNaN(date.getTime()) ? null : date
}

const requiredString = (msg = t.gfCampoObbligatorio) =>
  z
    .string()
    .min(1, msg)
    .refine((v) => v && v.trim() !== '', msg)

const optionalString = () => z.string().optional().or(z.literal(''))

const dateString = () => z.union([z.string().regex(dateRegex, t.gfFormatoData), z.literal('')])

/**
 * @param {{ departurePlaceRequired?: boolean, enableMultiPax?: boolean, isGoelett?: boolean }} options
 *   Su Goelett la trasferta non è un requisito: la Search chiede date e luoghi dei servizi,
 *   non quelli della trasferta MidOffice. Il formato e l'ordine delle date si controllano
 *   comunque se sono state scritte.
 */
export const buildTransferSchema = ({
  departurePlaceRequired = false,
  enableMultiPax = false,
  isGoelett = false
}) =>
  z
    .object({
      departureDate: isGoelett
        ? dateString()
        : dateString().refine((v) => !!v, t.gfCampoObbligatorio),
      arrivalDate: isGoelett
        ? dateString()
        : dateString().refine((v) => !!v, t.gfCampoObbligatorio),
      departurePlace: departurePlaceRequired && !isGoelett ? requiredString() : optionalString(),
      arrivalPlace: isGoelett ? optionalString() : requiredString(),
      numPax: enableMultiPax
        ? z.union([z.literal(''), z.string().regex(/^\d+$/, t.gfSoloNumeri)]).optional()
        : z.any().optional()
    })
    .superRefine((data, ctx) => {
      const dep = parseDate(data.departureDate)
      const arr = parseDate(data.arrivalDate)
      if (dep && arr && arr < dep) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['arrivalDate'],
          message: t.gfNonPrecedentePartenza
        })
      }
    })

const FIELDS = {
  departureDate: 'transferInfo.requestDataTransfer.departureDate',
  arrivalDate: 'transferInfo.requestDataTransfer.arrivalDate',
  departurePlace: 'transferInfo.requestDataTransfer.departurePlace',
  arrivalPlace: 'transferInfo.requestDataTransfer.arrivalPlace',
  numPax: 'transferInfo.infoPax.numPax'
}

const readPath = (state, path) => {
  let cursor = state
  for (const k of path.split('.')) cursor = cursor?.[k]
  return cursor
}

export const buildTransferDefaults = (state) => ({
  departureDate: readPath(state, FIELDS.departureDate) ?? '',
  arrivalDate: readPath(state, FIELDS.arrivalDate) ?? '',
  departurePlace: readPath(state, FIELDS.departurePlace) ?? '',
  arrivalPlace: readPath(state, FIELDS.arrivalPlace) ?? '',
  numPax:
    readPath(state, FIELDS.numPax) === undefined || readPath(state, FIELDS.numPax) === null
      ? ''
      : String(readPath(state, FIELDS.numPax))
})

export const TRANSFER_LOCATIONS = FIELDS
